import {
  Button,
  ButtonGroup,
  FlexBox,
  space,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ReactComponent as LightningIcon } from "../../public/material-icons/lightning-bolt.svg";
import type { LightStatus } from "../api/laboperator.types";
import type { OperationMode } from "../view-model";

type ModeButton = {
  mode: OperationMode | LightStatus;
  text: string;
};

const StyledGroupControl = styledComponents.div`
  aspect-ratio: 2 / 1;
  display: flex;
  flex-direction: column;
  gap: ${space(1)};
  padding: ${space(5)};
  border: 1px solid ${theme.colorPalette.grey200};
  overflow: hidden;
`;

type GroupControlProps = {
  onClick: (
    mode: OperationMode | LightStatus,
    type: "light" | "operation_status"
  ) => void;
  // Mode/light shared by ALL devices in the group (null = mixed / unknown). The
  // matching button is highlighted + disabled — setting it would be a no-op.
  activeMode?: OperationMode | null;
  activeLight?: LightStatus | null;
};

export const GroupControl = ({
  activeLight,
  activeMode,
  onClick,
}: GroupControlProps) => {
  const { t } = useTranslation();

  const operationButtons: ModeButton[] = [
    {
      mode: "off",
      text: t("detail.mode.off"),
    },
    {
      mode: "low",
      text: t("detail.mode.low"),
    },
    {
      mode: "normal",
      text: t("detail.mode.normal"),
    },
    {
      mode: "high",
      text: t("detail.mode.high"),
    },
  ];

  const lightButtons: ModeButton[] = [
    {
      mode: 1,
      text: t("detail.mode.on"),
    },
    {
      mode: 0,
      text: t("detail.mode.off"),
    },
  ];

  return (
    <StyledGroupControl>
      <FlexBox alignItems="center" gap={space(2)}>
        <LightningIcon />
        <Typography>{t("dashboard.groupcontrol")}</Typography>
      </FlexBox>

      <Typography size="xs">{t("channel.operation_status")}</Typography>
      <ButtonGroup isCompact>
        {operationButtons.map((operationButton) => (
          <Button
            disabled={operationButton.mode === "off"}
            key={operationButton.mode}
            onClick={() => onClick(operationButton.mode, "operation_status")}
            variant={
              operationButton.mode === activeMode ? "primary" : "secondary"
            }
          >
            {operationButton.text}
          </Button>
        ))}
      </ButtonGroup>
      <Typography size="xs">{t("channel.light")}</Typography>
      <ButtonGroup isCompact>
        {lightButtons.map((lightButton) => (
          <Button
            key={lightButton.mode}
            onClick={() => onClick(lightButton.mode, "light")}
            variant={lightButton.mode === activeLight ? "primary" : "secondary"}
          >
            {lightButton.text}
          </Button>
        ))}
      </ButtonGroup>
    </StyledGroupControl>
  );
};
